import { Link } from "react-router-dom";
import { FileCheck, Scale, ClipboardCheck, BarChart3, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const services = [
  {
    title: "Carbon Footprint Assessment",
    description: "Comprehensive measurement of your organization's greenhouse gas emissions across Scope 1, 2, and 3.",
    icon: ClipboardCheck,
    features: [
      "Complete GHG inventory",
      "Supply chain emissions mapping",
      "Baseline establishment",
      "Reduction opportunity identification"
    ]
  },
  {
    title: "Emissions Reporting",
    description: "Structured reporting aligned with global standards and regulatory requirements.",
    icon: BarChart3,
    features: [
      "GRI Standards compliance",
      "CDP reporting",
      "TCFD alignment",
      "Regulatory submissions"
    ]
  },
  {
    title: "CBAM Compliance Support",
    description: "End-to-end guidance for Carbon Border Adjustment Mechanism compliance for EU exports.",
    icon: Scale,
    features: [
      "CBAM registration assistance",
      "Embedded emissions calculation",
      "Quarterly reporting support",
      "Certificate management"
    ]
  },
  {
    title: "Sustainability Advisory",
    description: "Strategic guidance on sustainability integration and climate transition planning.",
    icon: FileCheck,
    features: [
      "Net zero roadmap development",
      "Science-based target setting",
      "ESG strategy formulation",
      "Stakeholder engagement"
    ]
  }
];

const cbamIndustries = [
  "Iron & Steel",
  "Aluminium",
  "Cement",
  "Fertilizers",
  "Electricity",
  "Hydrogen"
];

export default function AdvisoryPage() {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="advisory-page">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-earth-50 to-forest-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white text-earth-700 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-sm">
            <Scale className="h-4 w-4" />
            CBAM & Carbon Compliance
          </div>
          <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            Carbon Compliance
            <span className="text-earth-700 block">Advisory Services</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Expert guidance for carbon footprint assessment, emissions reporting, 
            CBAM compliance, and sustainability strategy.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-earth-700 hover:bg-earth-800" data-testid="advisory-cta">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-slate-600">Comprehensive support for your climate compliance journey</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service) => (
              <Card key={service.title} className="h-full" data-testid={`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6 lg:p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 bg-earth-50 rounded-xl text-earth-700">
                      <service.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-montserrat text-xl font-bold text-slate-900">{service.title}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-earth-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CBAM Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-montserrat text-3xl font-bold text-slate-900 mb-6">
                CBAM Compliance for Indian Exporters
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                The EU's Carbon Border Adjustment Mechanism (CBAM) is now effective, 
                requiring importers to report embedded carbon emissions in covered goods. 
                Indian exporters in affected sectors must prepare for compliance to maintain 
                market access.
              </p>
              <div className="mb-6">
                <p className="font-semibold text-slate-900 mb-3">Covered Industries:</p>
                <div className="flex flex-wrap gap-2">
                  {cbamIndustries.map((industry) => (
                    <span key={industry} className="px-3 py-1 bg-earth-100 text-earth-700 rounded-full text-sm font-medium">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
              <Link to="/contact">
                <Button className="bg-earth-700 hover:bg-earth-800">
                  Get CBAM Compliance Support
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
              <h3 className="font-montserrat text-xl font-semibold text-slate-900 mb-6">CBAM Timeline</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Oct 2023 - Dec 2025</p>
                    <p className="text-sm text-slate-600">Transitional Phase - Reporting only</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-700 font-bold text-sm">!</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Jan 2026</p>
                    <p className="text-sm text-slate-600">Full CBAM implementation begins</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-500 font-bold text-sm">→</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">2026+</p>
                    <p className="text-sm text-slate-600">Certificate purchases required</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-earth-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-montserrat text-3xl font-bold mb-4">Ready to Start Your Compliance Journey?</h2>
          <p className="text-earth-100 mb-8 max-w-2xl mx-auto">
            Our expert team will guide you through every step of carbon compliance, 
            from initial assessment to ongoing reporting.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-earth-700 hover:bg-earth-50">
              Contact Our Advisory Team
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
